import React, { useState } from "react";
import {
  getGridSingleSelectOperators,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
} from "@mui/x-data-grid-premium";
import { Box } from "@mui/material";
import DataGridTable from "../../base/DataGridTable";
import { useDispatch, useSelector } from "../../../store";
import SearchResults from "../../../types/searchResults";
import TableData from "../../../types/tableData";
import { OrderDirection } from "../../../types/searchParams";
import { getTable, updateTableSearchParams } from "../../../slices/tile";

interface Props {
  id: number;
  tileTable: SearchResults;
  changePage: (pageNumber?: number, pageSize?: number) => void;
}

const TileTable: React.FC<Props> = (props) => {
  const { id, tileTable, changePage } = props;

  const dispatch = useDispatch();

  const { tileTableStatus, tableSearchParams } = useSelector(
    (store) => store.tiles
  );

  const [visibility, setVisibility] = useState(
    tileTable?.headers?.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {} as { [key: string]: boolean })
  );

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    const filterList = model.items;
    const filters: string[] = [];

    if (filterList) {
      filterList.forEach((filter) => {
        if (filter.value)
          if (Array.isArray(filter.value)) {
            filter.value.forEach((id) => {
              const newFilter = `${filter.field}:${id}`;
              filters.push(newFilter);
            });
          } else {
            const newFilter = `${filter.field}:${filter.value}`;
            filters.push(newFilter);
          }
      });
    }
    dispatch(
      updateTableSearchParams({
        ...tableSearchParams,
        pageNumber: 1,
        filters,
      })
    );
  };

  const handleSort = (orderBy?: string, orderDirection?: OrderDirection) => {
    dispatch(
      updateTableSearchParams({
        ...tableSearchParams,
        orderBy,
        orderDirection,
      })
    );
  };

  const formatHeader = (header: string): string => {
    return header
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  };

  const displayRows = (array: SearchResults) => {
    return (
      array.results?.map((o: TableData, i) => {
        const rowObject: Record<string, string | undefined> = {
          id: `${i}`,
        };
        o.values?.forEach((value, index) => {
          const key = array.headers![index] || `key${index + 1}`;
          rowObject[key] = value;
        });
        return rowObject;
      }) || []
    );
  };

  const rows =
    tileTable &&
    tileTable?.results?.map((row: TableData, index) => {
      const rowObject: Record<string, string | undefined> = {
        id: `${index}`,
      };
      row.values?.forEach((value, index) => {
        const key = tileTable.headers![index] || `key${index + 1}`;
        rowObject[key] = value;
      });

      return rowObject;
    });

  const columns =
    !!tileTable && tileTable?.headers
      ? tileTable?.headers?.map((header) => {
          let headerObj = {
            field: header,
            headerName: formatHeader(header),
            headerAlign: "center",
            align: "center",
            flex: 1,
            headerClassName: "header",
            type: "singleSelect",
            filterOperators: getGridSingleSelectOperators().filter(
              (o) => o.value === "isAnyOf"
            ),
          };
          return headerObj as GridColDef;
        })
      : [];

  const preExport = async () => {
    const response = await dispatch(
      getTable({
        search: {
          ...tableSearchParams,
          pageSize: tileTable.totalResults,
        },
        export: true,
      })
    );
    return response;
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DataGridTable
        allowExport
        columns={columns}
        rows={tileTable ? displayRows(tileTable) : []}
        loading={
          tileTableStatus === "loading" &&
          (!rows?.length || tileTable.id !== id)
        }
        page={tableSearchParams.pageNumber}
        pageSize={tableSearchParams.pageSize}
        totalRows={tileTable?.totalResults}
        changePage={changePage}
        columnVisibility={visibility}
        handleColumnVisibilityChange={(model) => setVisibility(model)}
        handleSort={handleSort}
        filterModel={filterModel}
        handleFilterModelChange={handleFilterModelChange}
        handlePreExport={preExport}
        displayRows={displayRows}
      />
    </Box>
  );
};

export default TileTable;
